/**
 * Tipos para el sistema de background jobs
 * Refleja JobsTrxBackgroundJobDto del API
 */

// ═══════════════════════════════════════════════════════════════════════════
// Enums
// ═══════════════════════════════════════════════════════════════════════════

export type JobStatus = "Queued" | "Running" | "Completed" | "Failed";

export type JobType =
  | "BookingReconciliation"
  | "ContractAssignment"
  | "EligibleInvoicesReport"
  | "CurrencyRateSync";

// ═══════════════════════════════════════════════════════════════════════════
// DTOs del API
// ═══════════════════════════════════════════════════════════════════════════

export interface BackgroundJobDto {
  id: number;
  jobType: JobType;
  status: JobStatus;
  entityId: number;
  progressPercent: number | null;
  progressMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  resultPayload: string | null;
  errorMessage: string | null;
}

/** DTO completo devuelto por POST /api/jobs (histórico) */
export interface HistoryJobDto extends BackgroundJobDto {
  requestedBy: string | null;
  inputPayload: string | null;
  progressUpdatedAt: string | null;
  isActive: boolean;
  createdOn: string;
  createdBy: string | null;
  modifiedOn: string;
  modifiedBy: string | null;
}

/** Interfaz común que tanto TrackedJob como HistoryJobDto satisfacen, usada por JobDetailDialog */
export interface JobDisplayData {
  id: number;
  jobType: JobType;
  status: JobStatus;
  entityId: number;
  startedAt: string | null;
  completedAt: string | null;
  progressPercent: number | null;
  progressMessage: string | null;
  errorMessage: string | null;
  resultPayload: string | null;
  parsedResult?: unknown;
  service: "core" | "finance";
  requestedBy?: string | null;
  createdOn?: string;
}

export interface EnqueueResponse {
  jobId: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Job trackeado en cliente
// ═══════════════════════════════════════════════════════════════════════════

export interface TrackedJob extends BackgroundJobDto {
  service: "core" | "finance";
  parsedResult?: unknown;
}

// ═══════════════════════════════════════════════════════════════════════════
// Error especializado para conflicto 409
// ═══════════════════════════════════════════════════════════════════════════

export class JobConflictError extends Error {
  activeJobId: number;

  constructor(activeJobId: number) {
    super(`Job conflict: active job ${activeJobId}`);
    this.name = "JobConflictError";
    this.activeJobId = activeJobId;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Result types por job type
// ═══════════════════════════════════════════════════════════════════════════

export interface JobEquipmentLinkDto {
  navieraBookingId?: number | null;
  navieraBookingReference?: string | null;
  repoDocBookingId?: number | null;
  repoDocBookingReference?: string | null;
  navieraBookingEquipmentId: number;
  repoDocBookingEquipmentId: number;
  containerId: string | null;
  navieraContainerId?: string | null;
  repoDocContainerId?: string | null;
  navieraEquipmentCode?: string | null;
  repoDocEquipmentCode?: string | null;
}

export interface ReconciliationMatchDetailDto {
  NavieraBookingId: number;
  NavieraBookingReference: string | null;
  RepoDocBookingId: number;
  RepoDocBookingReference: string | null;
  MatchLevel: number;
  EquipmentLinks?: JobEquipmentLinkDto[];
  equipmentLinks?: JobEquipmentLinkDto[];
}

export interface UnmatchedBookingDto {
  BookingId: number;
  BookingReference: string | null;
  Source?: string | null;
  FailureReason?: string | null;
}

export interface BookingReconciliationResultDto {
  TotalMatched: number;
  TotalPendingReview: number;
  TotalUnmatched: number;
  Message?: string | null;
  storagePath?: string | null;
  // Arrays detallados — vienen del endpoint /download, no del resultPayload inline
  Matches?: ReconciliationMatchDetailDto[];
  PendingReviewBookings?: ReconciliationMatchDetailDto[];
  UnmatchedBookings?: UnmatchedBookingDto[];
}

export interface EquipmentAssignmentDetailDto {
  BookingEquipmentId: number;
  EquipmentId: number;
  EquipmentCode: string | null;
  ContainerId: string | null;
  PreviousContractLineId: number | null;
  NewContractLineId: number | null;
  ContractNumber: string | null;
  Reason: string | null;
  WasUpdated: boolean;
}

export interface BookingAssignmentDetailDto {
  BookingId: number;
  BookingReference: string | null;
  OperationType: string | null;
  TotalEquipments: number;
  EquipmentsAssigned: number;
  EquipmentsWithoutContract: number;
  Status: string | null;
  EquipmentAssignments: EquipmentAssignmentDetailDto[] | null;
}

export interface AssignContractLinesResultDto {
  TotalBookingsProcessed: number;
  TotalEquipmentsProcessed: number;
  TotalEquipmentsUpdated: number;
  TotalEquipmentsSkipped: number;
  TotalEquipmentsUnchanged: number;
  IsPreview: boolean;
  Assignments: BookingAssignmentDetailDto[] | null;
}

export interface CarrierInvoiceLineDto {
  CarrierInvoiceLineId: number;
  ConceptCode: string;
  ConceptDescription: string;
  Quantity: { source: string; parsedValue: number } | null;
  UnitPrice: { source: string; parsedValue: number } | null;
  LineAmount: { source: string; parsedValue: number } | null;
  TaxRate: number | null;
  TaxAmount: { source: string; parsedValue: number } | null;
}

export interface MappingWarningDto {
  CarrierInvoiceLineId: number;
  ConceptId: number;
  ConceptCode: string;
  ConceptDescription: string;
  Classification: string;
  Reason: string;
}

export interface EligibleInvoiceItemDto {
  CarrierInvoiceId: number;
  InvoiceNumber: string;
  InvoiceDate: string;
  BookingId: number;
  BookingReference: string | null;
  BookingSource?: string | null;
  LinkedBookingId?: number | null;
  TotalEquipment?: number | null;
  CurrencyCode: string;
  PendingLines: number;
  TrafficType?: string | null;
  ContractLineIds?: number[] | null;
  Lines?: CarrierInvoiceLineDto[] | null;
  MappingWarnings?: MappingWarningDto[] | null;
}

export interface DiscardedInvoiceItemDto extends EligibleInvoiceItemDto {
  Reason: string;
}

export interface EligibleInvoicesResultDto {
  EntityId: number;
  GeneratedAt: string;
  GeneratedBy: string | null;
  TotalAnalyzed: number;
  TotalEligible: number;
  TotalEligibleLines: number;
  TotalDiscarded: number;
  Eligible: EligibleInvoiceItemDto[];
  Discarded: DiscardedInvoiceItemDto[];
}

export interface CurrencyRateSyncResultDto {
  RatesSynced: number;
  SyncDate: string;
  Message: string;
}
